/**
 * `useCaseFilesSortStore` — per-folder sort for the consolidated case-files
 * view (plan §6 F7, operator-specified 2026-07-31).
 *
 * ## Scope: per (CASE, FOLDER) — cross-case was OVERRULED
 *
 * The ruling reads "per-folder, not per-case, not per-org", and separately
 * overruled the cross-case recommendation. Those resolve one way: the FOLDER
 * is the unit of memory (not one setting for a whole case), and the CASE is
 * the scope (the same-named folder in another case does not inherit). Hence a
 * `${caseKey}:${folderKey}` key. Org scope uses the literal `"org"` in the
 * caseKey position, matching the existing `scopeKeySegment` convention.
 *
 * ## Storage: localStorage, deviations only
 *
 * A cross-tab UI preference, so localStorage (the `useContextPaneStore
 * .collapsed` precedent) rather than the sessionStorage used for per-tab work
 * context. Only a DEVIATION from the default is written: the common case
 * costs nothing, and case-scoping multiplies the key space, which is what
 * makes the eviction cap load-bearing rather than theoretical — a case the
 * lawyer never reopens must not hold storage forever.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";

export type SortMode = "alpha" | "recency";
export type SortDir = "asc" | "desc";

export interface FolderSort {
  active: SortMode;
  /** Each mode remembers its OWN direction, so switching modes and back
   *  restores what the user last chose rather than resetting. */
  alphaDir: SortDir;
  recencyDir: SortDir;
}

/** Recency-descending (newest first) — the alphabetical handle wall is one of
 *  the problems this view exists to fix, not a default to preserve. */
export const DEFAULT_SORT: Readonly<FolderSort> = {
  active: "recency",
  alphaDir: "asc",
  recencyDir: "desc",
};

/** Bounded record size; oldest WRITE evicted first. */
export const SORT_CAP = 200;

/** Storage key for one folder within one case scope. */
export function sortKeyFor(caseKey: string, folderKey: string): string {
  return `${caseKey}:${folderKey}`;
}

/** True when a setting is indistinguishable from the default (so it should not
 *  be stored, and an existing entry should be dropped). */
export function isDefaultSort(s: FolderSort): boolean {
  return (
    s.active === DEFAULT_SORT.active &&
    s.alphaDir === DEFAULT_SORT.alphaDir &&
    s.recencyDir === DEFAULT_SORT.recencyDir
  );
}

/**
 * The control's interaction rule (F7): clicking the INACTIVE mode activates it
 * keeping that mode's last direction; clicking the ACTIVE mode flips its
 * direction. Pure so the behaviour is pinned without a DOM.
 */
export function toggleSort(current: FolderSort, mode: SortMode): FolderSort {
  if (current.active !== mode) return { ...current, active: mode };
  const flip = (d: SortDir): SortDir => (d === "asc" ? "desc" : "asc");
  return mode === "alpha"
    ? { ...current, alphaDir: flip(current.alphaDir) }
    : { ...current, recencyDir: flip(current.recencyDir) };
}

/**
 * Deviations-only write with MRU eviction. Re-writing an existing key moves it
 * to the most-recent position (delete-then-set), so the cap drops genuinely
 * stale entries rather than whichever happened to be inserted first.
 *
 * Pure — exported for unit test.
 */
export function putSort(
  record: Readonly<Record<string, FolderSort>>,
  key: string,
  next: FolderSort,
  cap = SORT_CAP,
): Record<string, FolderSort> {
  const out: Record<string, FolderSort> = {};
  for (const [k, v] of Object.entries(record)) if (k !== key) out[k] = v;
  // A default is not stored at all — and an existing entry that returns to the
  // default is REMOVED, so the record shrinks back instead of accumulating
  // no-op rows.
  if (!isDefaultSort(next)) out[key] = next;
  const entries = Object.entries(out);
  if (entries.length <= cap) return out;
  return Object.fromEntries(entries.slice(entries.length - cap));
}

/**
 * Comparator for one group's files.
 *
 * `labelOf` supplies the DISPLAYED label — alphabetical sorting must key on
 * exactly what the lawyer reads, never on the handle or the on-disk stem;
 * ordering by a hidden key reads as broken ordering.
 *
 * Absent `modifiedAt` sorts LAST in both directions: an unknown date is not
 * "oldest", and letting it masquerade as an extreme would put unknowns at the
 * top of a newest-first list.
 *
 * Pure — exported for unit test.
 */
export function compareBySort<T extends { path: string; modifiedAt?: string }>(
  a: T,
  b: T,
  sort: FolderSort,
  labelOf: (f: T) => string,
  locale: string,
): number {
  const la = labelOf(a);
  const lb = labelOf(b);
  const tiebreak = () =>
    la.localeCompare(lb, locale, { sensitivity: "base", numeric: true }) ||
    a.path.localeCompare(b.path);

  if (sort.active === "alpha") {
    const cmp = la.localeCompare(lb, locale, { sensitivity: "base", numeric: true });
    if (cmp !== 0) return sort.alphaDir === "asc" ? cmp : -cmp;
    return a.path.localeCompare(b.path);
  }

  const ta = a.modifiedAt ? Date.parse(a.modifiedAt) : Number.NaN;
  const tb = b.modifiedAt ? Date.parse(b.modifiedAt) : Number.NaN;
  const aBad = Number.isNaN(ta);
  const bBad = Number.isNaN(tb);
  if (aBad && bBad) return tiebreak();
  if (aBad) return 1;
  if (bBad) return -1;
  if (ta !== tb) return sort.recencyDir === "desc" ? tb - ta : ta - tb;
  return tiebreak();
}

interface CaseFilesSortStore {
  byFolder: Record<string, FolderSort>;
  /** Resolved setting for a folder — the default when nothing is stored. */
  sortFor: (caseKey: string, folderKey: string) => FolderSort;
  /** Apply the F7 click rule and persist the result. */
  toggle: (caseKey: string, folderKey: string, mode: SortMode) => void;
  clear: () => void;
}

export const useCaseFilesSortStore = create<CaseFilesSortStore>()(
  persist(
    (set, get) => ({
      byFolder: {},
      sortFor: (caseKey, folderKey) =>
        get().byFolder[sortKeyFor(caseKey, folderKey)] ?? { ...DEFAULT_SORT },
      toggle: (caseKey, folderKey, mode) => {
        const key = sortKeyFor(caseKey, folderKey);
        const current = get().byFolder[key] ?? { ...DEFAULT_SORT };
        set((s) => ({ byFolder: putSort(s.byFolder, key, toggleSort(current, mode)) }));
      },
      clear: () => set({ byFolder: {} }),
    }),
    {
      name: "lexide:case-files-sort",
      storage: createJSONStorage(() => createTenantScopedStorage(() => localStorage)),
      partialize: (state) => ({ byFolder: state.byFolder }),
    },
  ),
);
